/*!
 * doTERRA Web-UI
 * http://www.doterra.com
 *
 * Copyright 2019 doTERRA Holdings, LLC
 * All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {Button} from './controls';
import {useFormField, useFormMutator, useFormState} from './form';
import {evaluateCreditCardNumber, maskedFormatter} from '../utils/validation';
import {useModalsMutator} from '../contexts/page';
import {ErrorModal} from './modals';
import {text} from '../utils/i18n';
import {Tooltip} from "react-tooltip";
import {DoterraReactSelectTheme} from "../utils/colors";
import ReactSelect from "react-select";
import {flushSync} from "react-dom";


const getOtherProps = (props) => {
    const other = {
        mode: props.mode,
        readOnly: props.readOnly,
        disabled: props.disabled,
        classes: props.classes,
        labelKey: props.labelKey,
        labelText: props.labelText,
        labelClasses: props.labelClasses,
        tabIndex: props.tabIndex,
        placeholder: props.placeholder,
        template: props.template,
        valid: props.valid,
        addClasses: props.valid,
        children: props.children,
        required: props.required,
        options: props.options
    };
    return Object.keys(other).reduce((obj, key) => {
        if (other[key] !== undefined) {
            obj[key] = other[key];
        }
        return obj;
    }, {});
};

/***********************************/
/** Field                         **/
/***********************************/
export const Field = (props) => {
    const {id, name, component, onFocus, onChange, onBlur, onKeyUp, maxLength, disabled, readOnly, autoCompleteValue, tooltipMessage, tooltipId, isIconEnabled, validateField, validateFieldFunc, isSearchable} = props;
    const [touched, setTouched] = useState(false);
    const formProps = useFormField(name);
    const field = {valid: formProps.valid, dirty: formProps.dirty};
    const {value, setValue} = formProps;
    const {setFieldValue} = useFormMutator();
    const otherProps = getOtherProps(props);

    const handleFocus = (e) => {
        flushSync(() => {
          setTouched(true);
        });
        if (onFocus) {
            onFocus(e);
        }
    };
    const handleChange = (e, overrideValue) => {
        let synthEvent = {...e, target: {...e.target, value: overrideValue ?? e.target.value}};
      flushSync(() => {
        setValue(synthEvent.target.value);
      });
        if (onChange) {
            onChange(synthEvent);
        }
    };
    const handleBlur = (e) => {
        if (onBlur) {
            onBlur(e);
        }
    };

    const handleKeyUp = (e) => {
        if (onKeyUp) {
            onKeyUp(e);
        }
    };

    const Component = component;
    return (<Component id={id} name={name} value={value} field={field} touched={touched} onFocus={handleFocus} onChange={handleChange} onBlur={handleBlur}
                       onKeyUp={handleKeyUp} maxLength={maxLength} setFieldValue={setFieldValue} disabled={disabled} readOnly={readOnly}
                       autoCompleteValue={autoCompleteValue} tooltipMessage={tooltipMessage} tooltipId={tooltipId} isIconEnabled={isIconEnabled} validateField={validateField} validateFieldFunc={validateFieldFunc} isSearchable={isSearchable} {...otherProps}/>);
};


/***********************************/
/** TextField                     **/
/***********************************/
export const TextField = (props) => {
    const {
        id,
        refID,
        name,
        value,
        readOnly,
        disabled,
        classes,
        labelKey,
        labelClasses,
        tabIndex,
        placeholder,
        template,
        onFocus,
        onChange,
        onBlur,
        onKeyUp,
        field,
        maxLength,
        required,
        valid,
        tooltipMessage,
        tooltipId
   , isIconEnabled, validateField } = props;
    const mask = template ? (template.restrictedMask || template.mask || undefined) : undefined;
    const upperCase = template && template.upperCase ? template.upperCase : undefined;
    let isValid = true;
    if (props.valid !== undefined) {
        isValid = props.valid;
    } else if (field !== undefined) {
        isValid = field.valid;
    }

    if(validateField && isValid) {
      isValid = false;
    }

    let disabledField = false;
    if (disabled) {
        disabledField = disabled;
    }

    const [active, setActive] = useState(false);

    let classNames = `controls${isValid ? '' : ' has-error'}`;
    let labelClassNames = '';
    if (labelClasses && labelClasses.length > 0) {
        labelClassNames += ` ${labelClasses}`;
    }
    if (active || (value && value.length > 0)) {
        labelClassNames += ` label-active`;
    }

    const handleFocus = (e) => {
      flushSync(() => {
        setActive(true);
      });
        if (onFocus) {
            onFocus(e);
        }
    };
    const handleChange = (e) => {
        if (mask) {
            const backspace = e.nativeEvent.inputType === 'deleteContentBackward';
            e.target.value = maskedFormatter(mask, e.target.value, backspace, upperCase);
        }
        if (onChange) {
            onChange(e);
        }
    };
    const handleBlur = (e) => {
        flushSync(() => {
          setActive(false);
        });
        if (onBlur) {
            onBlur(e);
        }
    };

    const handleKeyUp = (e) => {
        if (onKeyUp) {
            onKeyUp(e);
        }
    };

    const LabelFieldTag = (tooltipMessage && tooltipId)?
        <><label htmlFor={id} className={labelClassNames} data-tooltip-content={tooltipMessage} data-tooltip-id={tooltipId}>{text(labelKey)}</label><Tooltip id={tooltipId} effect="solid" type="light"/></> :
        <label htmlFor={id} className={labelClassNames}>{text(labelKey)}</label>;

    let input_field_type = "text";
    if(id === 'changeNotificationsId') {
      input_field_type = "email";
    }

    return (<>
            <div className="control-group">
                <div className={classNames}>
                    <input ref={refID} id={id} type={input_field_type} className={classes} name={name} maxLength={maxLength} value={value} disabled={disabledField}
                           placeholder={placeholder} readOnly={readOnly} tabIndex={tabIndex}
                           onFocus={handleFocus}
                           onChange={handleChange}
                           onBlur={handleBlur} onKeyUp={handleKeyUp} required={required}
                    />
                    {(labelKey && labelKey.length > 0) && LabelFieldTag}
                    {isIconEnabled && <i className="icon--tooltip icon--md" data-toggle="tooltip" data-container="body" title={tooltipMessage}></i>}
                </div>
            </div>
            {isValid && <div className="help-inline"/>}
        </>);
};

/***********************************/
/** SwitchSelector                     **/
/***********************************/
export const SwitchSelector = (props) => {
  const { id_true, id_false, name, trueLabel, falseLabel, disabled, classes, defaultValue, onChange } = props;

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <>
      <div className="switch_selector brand-selector">
        <input role="button" id={id_true} type="radio" className={classes} name={name} value={true} disabled={disabled}
               tabIndex={0}
               onChange={handleChange}
               checked={defaultValue}
        />
        <label htmlFor={id_true}>{trueLabel}</label>
        <input role="button" id={id_false} type="radio" className={classes} name={name} value={false} disabled={disabled}
               tabIndex={0}
               onChange={handleChange}
               checked={!defaultValue}
        />
        <label htmlFor={id_false}>{falseLabel}</label>
        <span className="toggle-outside"><span className="isVisible toggle-inside"/></span>
      </div>
    </>
  );
};

/***********************************/
/** CardCreditField               **/
/***********************************/
const CC_MSG_INVALID = 'text.paymentManagement.invalidCard';
const CC_MSG_TOKENIZATION_ERROR = 'card.tokenization.error';
const CC_MSG_MAP = {
    'Invalid Credit card number': CC_MSG_INVALID,
    'Credit card value is empty': CC_MSG_INVALID,
    'Masked value received, nothing to do': CC_MSG_TOKENIZATION_ERROR,
    'Already a token, nothing to do': CC_MSG_TOKENIZATION_ERROR
};

export const CreditCardField = (props) => {
    let {
        id,
        name,
        mode,
        value,
        disabled,
        labelKey,
        labelClasses,
        tabIndex,
        submitId,
        onFocus,
        onChange,
        onBlur,
        field,
        cardTypeName,
        setFieldValue,
        autoCompleteValue
   , validateFieldFunc } = props;
    id = id ? id : 'ccNumber';
    name = name ? name : 'ccNumber';
    cardTypeName = cardTypeName ? cardTypeName : 'cardType';
    const {showGlobalModal, hideGlobalModal} = useModalsMutator();

    let valid = true;
    if (props.valid !== undefined) {
        valid = props.valid;
    } else if (field !== undefined) {
        valid = field.valid;
    }

    if(!valid) {
      validateFieldFunc(false);
    }

    const [active, setActive] = useState(false);

    const addMode = mode === 'addCard' || mode === 'crmaddCard';
    const readOnly = !addMode;
    const placeholder = addMode ? '0000000000000000' : '';
    const classes = `form-control${addMode ? ' required' : ''}`;
    let classNames = `controls${valid ? '' : ' has-error'}`;
    let labelClassNames = '';
    if (labelClasses && labelClasses.length > 0) {
        labelClassNames += ` ${labelClasses}`;
    }
    if (active || (value && value.length > 0)) {
        labelClassNames += ` label-active`;
    }

    const handleMessageListener = useCallback((e) => {
        const {data} = e;
        if (typeof data === 'string' && data.substring(0, 8) === 'TOKEN: *') {
          setFieldValue(name, data.substring(7));
          validateFieldFunc(true);
        }
    });

    useEffect(() => {
        if (field) {
            window.addEventListener("message", handleMessageListener);
            return () => {
                window.removeEventListener("message", handleMessageListener);
            };
        }
    });

    useEffect(() => {
        window.handleDisplayMessage = (where, type, msg) => {
            const modalName = 'tokenization-error-modal';
            let ccErrorMsg = text(CC_MSG_MAP[msg] || CC_MSG_TOKENIZATION_ERROR);
            console.log("Paymetric Tokenize Error: " + msg.toString());

            const closeHandler = () => {
                hideGlobalModal(modalName);
                setFieldValue(name, "");
                document.getElementById(id).focus();
            };
            showGlobalModal(modalName, <ErrorModal text={ccErrorMsg} close={closeHandler}/>, true);
        };
        return () => {
            window.handleDisplayMessage = undefined;
        };
    });

    const handleFocus = useCallback((e) => {
        if (e.target.value.startsWith('************')) {
            e.target.select();
        }
        flushSync(() => {
          setActive(true);
        });
        if (onFocus) {
            onFocus(e);
        }
    });

    const handleChange = useCallback((e) => {
        const re = /^[0-9\b]+$/;
        if (e.target.value === '' || re.test(e.target.value)) {
            if (onChange) {
                onChange(e);
            }
        }
    });

    const [shouldInit, setShouldInit] = useState(false);

    const handleBlur = useCallback((e) => {
    const { type, valid } = evaluateCreditCardNumber(e.target.value);

    flushSync(() => {
      setActive(false);
      if (valid) {
        setFieldValue(cardTypeName, type === 'mastercard' ? 'master' : type);
        setShouldInit(true);
      }
    });

    onBlur?.(e);
  }, [evaluateCreditCardNumber, onBlur]);

   useEffect(() => {
      if (!shouldInit) return;

      // Safe to call iframe tokenization API now
      InitForTokenization(true, name, submitId, 'xiFrame', 'light', 'fade');
      window.SendIFrameMessage();
      // Reset the flag to avoid re-running
      setShouldInit(false);
    }, [shouldInit, name, submitId]);

    return (<>
            <div className="control-group">
                <div className={classNames}>
                    {autoCompleteValue ? <input id={id} type="text" className={classes} name={name} value={value} disabled={disabled}
                                                placeholder={placeholder} readOnly={readOnly} tabIndex={tabIndex}
                                                onFocus={handleFocus}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                autoComplete={autoCompleteValue}/> :
                        <input id={id} type="text" className={classes} name={name} value={value} disabled={disabled}
                               placeholder={placeholder} readOnly={readOnly} tabIndex={tabIndex}
                               onFocus={handleFocus}
                               onChange={handleChange}
                               onBlur={handleBlur}/>}

                    {(labelKey && labelKey.length > 0) && <label htmlFor={id} className={labelClassNames}>
                        {text(labelKey)}
                    </label>}
                </div>
            </div>
            {valid && <div className="help-inline"/>}
        </>);
};

/***********************************/
/** dropdown-select Wrapper          **/
/***********************************/
export const DoterraSelect = (props) => {
  const {id, name, value, disabled, addClasses, labelClasses, labelText, options, onFocus, onChange, onBlur, field, isSearchable} = props;
  const selectClasses = `doterra-select-react greyed-out${addClasses ? ' ' + addClasses : ''}`;
  let valid = true;
  if (props.valid !== undefined) {
    valid = props.valid;
  } else if (field !== undefined) {
    valid = field.valid;
  }
  const controlClasses = `controls${valid ? '' : ' has-error'}`;

  return (
    <div className="control-group">
      <div className={controlClasses}>
        <ReactSelect
          id={id}
          name={name}
          disabled={disabled}
          className={selectClasses}
          classNamePrefix="doterra-select-react"
          aria-label={labelText}
          options={options}
          value={options?.find(c => c.value === value)}
          onChange={(e) => {onChange(e, e.value);}}
          theme={DoterraReactSelectTheme}
          components={{ DropdownIndicator:() => null, IndicatorSeparator:() => null }}
          isSearchable={isSearchable}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              margin: '0 !important',
              boxShadow: state.isFocused ? "none" : "none"
            }),
            option: (baseStyles, state) => ({
              ...baseStyles,
              backgroundColor: state.isFocused ? "none" : "none",
              fontWeight: state.isFocused ? "bold" : "none",
              color: state.isFocused ? "initial" : "initial"
            })
          }}
          placeholder={null}
        />
        {(labelText && labelText.length > 0) &&
          <label htmlFor={id} className={labelClasses}>
            {labelText}
          </label>
        }
      </div>
    </div>
  );
};

/***********************************/
/** Checkbox                      **/
/***********************************/
export const Checkbox = (props) => {
    const {id, name, value, classes, disabled, tabIndex, labelClasses, labelKey, wrapperClasses, onFocus, onChange, onBlur, defaultChecked, field} = props;
    let valid = true;
    if (props.valid !== undefined) {
        valid = props.valid;
    } else if (field !== undefined) {
        valid = field.valid;
    }
    const checkedProp = field !== undefined ? {checked: value} : {defaultChecked: defaultChecked !== undefined ? defaultChecked : false};

    const handleChange = (e) => {
        if (onChange) {
            onChange(e, e.target.checked);
        }
    };

    const classNames = `control-group${valid ? '' : ' error'}`;
    let finalWrapperClasses = `controls custom-checkbox${wrapperClasses && wrapperClasses.length > 0 ? wrapperClasses : ''}`;
    return (<>
            <div className={classNames}>
                <div className={finalWrapperClasses}>
                    <input id={id} name={name} type="checkbox" className={classes} disabled={disabled} tabIndex={tabIndex} {...checkedProp} onFocus={onFocus}
                           onChange={handleChange} onBlur={onBlur}/>
                    <label htmlFor={id} className={labelClasses}>
                        {text(labelKey)}
                    </label>
                </div>
            </div>
            {!valid && <div className="help-inline"/>}
        </>);

};


/***********************************/
/** Hidden                        **/
/***********************************/
export const Hidden = (props) => {
    const {name, value} = props;
    return (<input name={name} type='hidden' value={value}/>);
};


/***********************************/
/** Submit button                 **/
/***********************************/

export const SubmitButton = (props) => {
    const {id, onClick, text, classes} = props;
    const {isValid} = useFormState();
    const disabled = !isValid;

    return <Button id={id} classes={classes} type="submit" onClick={onClick} disabled={disabled} text={text}/>;
};
